import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { setBudgetData } from "@/redux/slices";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { schema } from "./validation";
import {
  AUTO_APPROVAL,
  AUTO_APPROVAL_LEVEL,
  BUDGET_ID,
  BUDGET_QTY,
  FINANCIAL_YEAR,
  GL_MASTER_ID,
  TOLERANCE,
  TRANSACTION_RATE,
  YEARLY_AMT,
} from "./consts";
import {
  getRequiredFields,
} from "@/lib/utils";
import {
  useAddLineItemMutation,
  useAddMonthlyDistributionMutation,
  useGetBudgetById,
  useGetGlEntriesByBudgetId,
  useGetLineItemById,
  useGetMonthDistributionByLineItemId,
  useUpdateLineItemMutation,
  useUpdateMonthlyDistributionMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  AMOUNT_1,
  AMOUNT_10,
  AMOUNT_11,
  AMOUNT_12,
  AMOUNT_2,
  AMOUNT_3,
  AMOUNT_4,
  AMOUNT_5,
  AMOUNT_6,
  AMOUNT_7,
  AMOUNT_8,
  AMOUNT_9,
} from "@/components/MonthlyDistribution/consts";

const useAddEditLineItem = () => {
  const { budgetId, action, id, lineItemId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isFromTolerance = location?.pathname?.includes("tolerance");
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const isView = lineItemId && action == "view";
  const isAdd = !lineItemId;
  const [glKey, setGlKey] = useState<number>(Math.random() * 4);
  const [approveLevelKey, setApproveLevelKey] = useState<number>(
    Math.random() * 3
  );
  const [openDistributionDialog, setOpenDistributionDialog] =
    useState<boolean>(false);
  const [monthlyDistribution, setMonthlyDistribution] = useState<any>(null);
  const [isLineItemInitialized, setIsLineItemInitialized] = useState(false);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const storedBudgetData = useSelector(
    (state: RootState) => state.currentData.budgetData
  );
  const communityId = (id || communityDetail?.communityId) as string;
  const { data: budgetInfo } = useGetBudgetById({
    communityId,
    budgetId: budgetId as string,
    queryParams: {
      enabled: !!(communityId && budgetId),
    },
  });
  const budgetData = budgetInfo?.data || storedBudgetData;

  useEffect(() => {
    if (budgetInfo?.data) {
      dispatch(setBudgetData(budgetInfo.data));
    }
  }, [budgetInfo, dispatch]);

  const form = useForm<any>({
    defaultValues: {
      [BUDGET_ID]: "",
      [FINANCIAL_YEAR]: budgetData?.[FINANCIAL_YEAR]?.toString(),
      [GL_MASTER_ID]: "",
      [TRANSACTION_RATE]: "0",
      [BUDGET_QTY]: "0",
      [TOLERANCE]: "0",
      [YEARLY_AMT]: "0",
      [AUTO_APPROVAL]: false,
      [AUTO_APPROVAL_LEVEL]: "",
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const title = useMemo(() => {
    return <div className="flex items-center">
      {isAdd
        ? `Add Line Item`
        : isView
        ? `View Line Item`
        : `Edit Line Item`}
      {(budgetData?.budgetName || budgetData?.year || budgetData?.versionNo) && (
        <span className="text-sm text-primary font-bold ml-3">
          Budget - {budgetData?.budgetName || "-"}{" "}
          <span className="font-normal">|</span> Financial Year -{" "}
          {budgetData?.year || "-"} <span className="font-normal">|</span>{" "}
          Version - {budgetData?.versionNo ?? "-"}
        </span>
      )}
    </div>
  }, [budgetData, isAdd, isView]);

  const { data: glMasterListing, isLoading: isGlMasterLoading } =
    useGetGlEntriesByBudgetId({
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!budgetId,
      },
    });

  const { data: lineItemData, isLoading: isLineItemDataLoading } =
    useGetLineItemById({
      budgetId: budgetId as string,
      id: lineItemId as string,
      queryParams: {
        enabled: !!lineItemId,
      },
    });

  const { data: monthlyDistributionData, isLoading: isDistributionLoading } =
    useGetMonthDistributionByLineItemId({
      lineItemId: lineItemId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!lineItemId,
      },
    });

  useEffect(() => {
    if (monthlyDistributionData) {
      setMonthlyDistribution(monthlyDistributionData?.data);
    }
  }, [monthlyDistributionData]);

  const yearlyAmount = useWatch({
    control: form.control,
    name: YEARLY_AMT,
  });

  useEffect(() => {
    if (yearlyAmount != lineItemData?.data?.[YEARLY_AMT]) {
      const divide = parseInt(yearlyAmount) / 12;
      const amt = parseFloat(divide.toString()).toFixed(2);
      setMonthlyDistribution({
        [AMOUNT_1]: amt?.toString(),
        [AMOUNT_2]: amt?.toString(),
        [AMOUNT_3]: amt?.toString(),
        [AMOUNT_4]: amt?.toString(),
        [AMOUNT_5]: amt?.toString(),
        [AMOUNT_6]: amt?.toString(),
        [AMOUNT_7]: amt?.toString(),
        [AMOUNT_8]: amt?.toString(),
        [AMOUNT_9]: amt?.toString(),
        [AMOUNT_10]: amt?.toString(),
        [AMOUNT_11]: amt?.toString(),
        [AMOUNT_12]: amt?.toString(),
      });
    }
  }, [yearlyAmount]);

  const storedLineItemList = useSelector(
    (state: RootState) => state.currentData.lineItemList
  );

  useEffect(() => {
    if (budgetData) {
      form.setValue(BUDGET_ID, budgetData[BUDGET_ID] || "");
      form.setValue(FINANCIAL_YEAR, budgetData[FINANCIAL_YEAR]?.toString() || "");
    }
  }, [budgetData, form]);

  useEffect(() => {
    if (!isLineItemInitialized && lineItemData && budgetData) {
      form.reset({
        [BUDGET_ID]: budgetData?.[BUDGET_ID] || "",
        [FINANCIAL_YEAR]: budgetData?.[FINANCIAL_YEAR]?.toString() || "",
        [GL_MASTER_ID]: lineItemData?.data?.[GL_MASTER_ID]?.toString() || "",
        [TRANSACTION_RATE]:
          lineItemData?.data?.[TRANSACTION_RATE]?.toString() || "0",
        [BUDGET_QTY]: lineItemData?.data?.[BUDGET_QTY]?.toString() || "0",
        [TOLERANCE]: lineItemData?.data?.[TOLERANCE]?.toString() || "0",
        [YEARLY_AMT]: lineItemData?.data?.[YEARLY_AMT]?.toString() || "0",
        [AUTO_APPROVAL]: !!lineItemData?.data?.[AUTO_APPROVAL],
        [AUTO_APPROVAL_LEVEL]:
          lineItemData?.data?.[AUTO_APPROVAL_LEVEL]?.toString() || "",
      });
      setGlKey(Math.random() * 4);
      setApproveLevelKey(Math.random() * 3);
      setIsLineItemInitialized(true);
    }
  }, [isLineItemInitialized, lineItemData, budgetData]);

  useEffect(() => {
    form.setValue(
      YEARLY_AMT,
      (
        Number(form.watch(TRANSACTION_RATE)) * Number(form.watch(BUDGET_QTY))
      )?.toString()
    );
  }, [form.watch(TRANSACTION_RATE), form.watch(BUDGET_QTY)]);

  // add monthly distribution
  const { mutate: addDistributionInfo, isPending: isAddDistributionPending } =
    useAddMonthlyDistributionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["line-items-list", budgetId],
        });
        onBackClick();
        toast.success("Line Item Added Successfully");
      },
    });

  // update monthly distribution
  const {
    mutate: updateDistributionInfo,
    isPending: isUpdateDistributionPending,
  } = useUpdateMonthlyDistributionMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["line-items-list", budgetId],
      });
      onBackClick();
      toast.success("Line Item Updated Successfully");
    },
  });

  // add line items
  const { mutate: addInfo, isPending: isAddPending } = useAddLineItemMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data) => {
      let obj = null;
      if (monthlyDistribution) {
        obj = monthlyDistribution;
      } else {
        const divide = parseInt(data.data.data?.[YEARLY_AMT]) / 12;
        const amt = parseFloat(divide.toString()).toFixed(2);
        obj = {
          [AMOUNT_1]: amt?.toString(),
          [AMOUNT_2]: amt?.toString(),
          [AMOUNT_3]: amt?.toString(),
          [AMOUNT_4]: amt?.toString(),
          [AMOUNT_5]: amt?.toString(),
          [AMOUNT_6]: amt?.toString(),
          [AMOUNT_7]: amt?.toString(),
          [AMOUNT_8]: amt?.toString(),
          [AMOUNT_9]: amt?.toString(),
          [AMOUNT_10]: amt?.toString(),
          [AMOUNT_11]: amt?.toString(),
          [AMOUNT_12]: amt?.toString(),
        };
      }
      addDistributionInfo({
        budgetId: budgetId as string,
        lineItemId: data.data.data.budgetLineitemId,
        data: obj,
      });
    },
  });

  //update line items
  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateLineItemMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data) => {
        if (monthlyDistribution) {
          if (monthlyDistributionData?.data) {
            updateDistributionInfo({
              budgetId: budgetId as string,
              data: monthlyDistribution,
              lineItemId: data.data.data.budgetLineitemId,
            });
          } else {
            addDistributionInfo({
              budgetId: budgetId as string,
              lineItemId: data.data.data.budgetLineitemId,
              data: monthlyDistribution,
            });
          }
        } else {
          queryClient.invalidateQueries({
            queryKey: ["line-items-list", budgetId],
          });
          onBackClick();
          toast.success("Line Item Updated Successfully");
        }
      },
    });

  const onSaveMonthlyDistribution = (data: any) => {
    setMonthlyDistribution(data);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const budgetListPath = isMasterDataRoute
    ? `/${routes.admin}/${routes.masterData}/${routes.budgetMgmt}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${communityId}/${routes.budgetMgmt}`;
  const lineItemsPath = `${budgetListPath}/${budgetId}/${routes.lineItems}`;

  const breadcrumbDataTolerance = useMemo(() => {
    return [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.tolerance}`,
        name: <span>Tolerance 360 Report (Year - {budgetData?.year})</span>,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.tolerance}/${routes.budgetMgmt}/${budgetId}/${routes.lineItems}`,
        name: (
          <span>{`Line Items - ${(budgetData as any)?.budgetName || ""}`}</span>
        ),
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {isAdd
              ? `Add Line Item for Budget - ${budgetData?.budgetName}`
              : isView
              ? `View Line Item`
              : `Edit Line Item`}
          </span>
        ),
      },
    ];
  }, [budgetData]);

  const breadcrumbDataCommunity = useMemo(() => {
    const currentPage = {
      link: "",
      name: (
        <span className="text-primary">
          {isAdd
            ? `Add Line Item for Budget - ${budgetData?.budgetName}`
            : isView
            ? `View Line Item`
            : `Edit Line Item`}
        </span>
      ),
    };
    const crumbs = [
      {
        link: budgetListPath,
        name: `Budget Management - ${(budgetData as any)?.budgetName || ""}`,
      },
      {
        link: lineItemsPath,
        name: "Line Items",
      },
      currentPage,
    ];

    if (isMasterDataRoute) {
      return crumbs;
    }

    return [
      {
        link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}`,
        name: `Community - ${(communityDetail as any)?.name || ""}`,
      },
      ...crumbs,
    ];
  }, [budgetData, budgetListPath, communityDetail, isAdd, isMasterDataRoute, isView, lineItemsPath]);

  const breadcrumbData = isFromTolerance
    ? breadcrumbDataTolerance
    : breadcrumbDataCommunity;

  const onBackClickCommunity = () => {
    navigate(lineItemsPath);
  };

  const onBackClickTolerance = () => {
    navigate(
      `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.tolerance}/${routes.budgetMgmt}/${budgetId}/${routes.lineItems}`
    );
  };

  const onBackClick = isFromTolerance
    ? onBackClickTolerance
    : onBackClickCommunity;

  const onSubmit = (data: any) => {
    let _data = data;

    if (!_data[AUTO_APPROVAL_LEVEL]) {
      delete _data[AUTO_APPROVAL_LEVEL]
    }

    if (_data[AUTO_APPROVAL] && !_data[AUTO_APPROVAL_LEVEL]) {
      form.setError(AUTO_APPROVAL_LEVEL, { message: "This field is required" });
      return;
    }

    if (_data[TOLERANCE] && parseInt(_data[TOLERANCE] as string) > 100) {
      form.setError(TOLERANCE, { message: "Value should be < 100" });
      return;
    }

    if (lineItemId) {
      updateInfo({
        id: lineItemId as string,
        budgetId: budgetId as string,
        data: { ..._data, communityId: id },
      });
    } else {
      addInfo({ budgetId: budgetId as string, data: { ..._data } });
    }
  };

  const onMontyDistributionClick = () => {
    setOpenDistributionDialog(true);
  };

  return {
    breadcrumbData,
    onBackClick,
    isView,
    isAdd,
    onSubmit,
    form,
    requiredFields,
    onCancel: onBackClick,
    isAddPending,
    isUpdatePending,
    isLineItemDataLoading,
    budgetId,
    glMasterListing,
    isGlMasterLoading,
    budgetData,
    lineItemId,
    onMontyDistributionClick,
    openDistributionDialog,
    setOpenDistributionDialog,
    glKey,
    isAddDistributionPending,
    isUpdateDistributionPending,
    onSaveMonthlyDistribution,
    approveLevelKey,
    storedLineItemList,
    isDistributionLoading,
    monthlyDistribution,
    title
  };
};

export default useAddEditLineItem;
